import { AttributesFilter, attributesFilterSchema, constructTokenJsonPathFilter } from '@/app/utils/token-features'
import {
  GetProjectTokensQueryVariables,
  Order_By,
  Scalars,
  Tokens_Metadata_Bool_Exp,
  Tokens_Metadata_Order_By
} from '@/gql/generated/graphql'
import { AVERAGE_SLOT_TIME } from '@consts/common'
import { createSearchParamsCache, parseAsBoolean, parseAsInteger, parseAsJson, parseAsStringEnum } from 'nuqs/server'

export enum ValidSorting {
  invocation_asc = 'Number: Low to high',
  invocation_dsc = 'Number: High to low',
  price_asc = 'Price: Low to high',
  price_dsc = 'Price: High to low',
  recently_listed = 'Recently Listed'
}

export const NUM_TOKENS_PER_PAGE = 48

export const DEFAULT_SORT_ORDER = ValidSorting.price_asc

export const constructFullTokensOrderBy = (sortOrder: ValidSorting): Tokens_Metadata_Order_By[] => {
  switch (sortOrder) {
    case ValidSorting.invocation_asc:
      return [{ invocation: Order_By.AscNullsLast }]
    case ValidSorting.invocation_dsc:
      return [{ invocation: Order_By.DescNullsLast }]
    case ValidSorting.price_asc:
      return [{ list_eth_price: Order_By.AscNullsLast }, { invocation: Order_By.AscNullsLast }]
    case ValidSorting.price_dsc:
      return [{ list_eth_price: Order_By.DescNullsLast }, { invocation: Order_By.DescNullsLast }]
    case ValidSorting.recently_listed:
      return [{ list_creation_date: Order_By.DescNullsLast }]
    default:
      console.error('Error setting order by')
      return [{ list_creation_date: Order_By.DescNullsLast }, { invocation: Order_By.Asc }]
  }
}

export const tokenFilterParsers = {
  attributes: parseAsJson(attributesFilterSchema.parse),
  sort: parseAsStringEnum<ValidSorting>(Object.values(ValidSorting)).withDefault(ValidSorting.price_asc),
  page: parseAsInteger.withDefault(1),
  abListingsOnly: parseAsBoolean.withDefault(false)
}

export const tokenFilterCache = createSearchParamsCache(tokenFilterParsers)

export function constructGetTokensQueryVariables({
  projectId,
  tokenFilter,
  pageNumber,
  sortOrder,
  searchInvocationValues,
  abListingsOnly,
  walletAddresses
}: {
  // TODO: handle projectId or projectSlug
  projectId?: string
  pageNumber: number
  tokenFilter: AttributesFilter
  sortOrder?: ValidSorting | null
  searchInvocationValues: number[]
  abListingsOnly?: boolean | null
  walletAddresses?: string[]
}): GetProjectTokensQueryVariables {
  const orderBy = constructFullTokensOrderBy(sortOrder ?? DEFAULT_SORT_ORDER)

  // JSON path filter for token features
  // Features are stored in a JSONB field in our database so that
  // their structure can be defined by the artist.  As such in order to
  // filter by feature we need to use a json path string. See the postgres
  // documentation for more details.
  // https://www.postgresql.org/docs/12/functions-json.html#FUNCTIONS-JSON-PROCESSING-TABLE
  // Map the filters to a jsonpath string to be passed to token query
  const tokensJsonPathFilter: Scalars['jsonpath']['output'] = constructTokenJsonPathFilter(tokenFilter ?? {})

  // variable where
  const whereFilter: Tokens_Metadata_Bool_Exp = {
    project_id: { _eq: projectId }
  }
  if (searchInvocationValues.length) {
    whereFilter.invocation = {
      _in: [...searchInvocationValues]
    }
  }
  if (abListingsOnly) {
    whereFilter.list_platform = {
      _in: ['artblocks.io', 'Sansa']
    }
    whereFilter.list_price = {
      _is_null: false
    }
  }
  if (walletAddresses && walletAddresses.length > 0) {
    whereFilter.owner_address = {
      _in: walletAddresses.map((address) => address.toLowerCase())
    }
  }

  return {
    offset: (pageNumber - 1) * NUM_TOKENS_PER_PAGE,
    limit: NUM_TOKENS_PER_PAGE,
    filter: tokensJsonPathFilter,
    where: whereFilter,
    orderBy,
    ttl: AVERAGE_SLOT_TIME
  }
}
